/*
 * DatePicker
 * @module components
 * @flow
 */

import React from 'react';
import styled from 'styled-components/macro';
import { palette, font } from 'styled-theme';
import Calendar from 'react-calendar/dist/entry.nostyle';

// $FlowIssue
import { ReactComponent as IconLeft } from '@/icons/left.svg';
// $FlowIssue
import { ReactComponent as IconRight } from '@/icons/right.svg';

export type DatePickerProps = {
  value?: Date,
  locale?: string,
  onChange?: () => void,
  palette?: string,
};

/**
 * DatePicker React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {Date} [props.value] - calendar value
 * @param {string} [props.locale] - IETF language tag
 * @param {Function} [props.onChange = () => {}] - on change value callback
 * @param {string} [props.palette = 'primary'] - current QR Code palette
 * @example <caption>Simple usage</caption>
 * <DatePicker value={new Date()} />
 * @returns {React.Node<*>} React-component
 */
export const DatePicker = styled(Calendar).attrs(() => ({
  testid: 'datepicker',
  selectRange: false,
  prevLabel: <IconLeft />,
  prev2Label: false,
  nextLabel: <IconRight />,
  next2Label: false,
  minDetail: 'month',
}))`
  &.react-calendar {
    padding: 38px 17px 24px;
    width: 280px;
    text-align: center;
    text-transform: uppercase;
  }

  button {
    border: 0;
    font-size: 16px;
    line-height: 0.88;
    color: ${palette('grayscale', 0)};
    font-family: ${font('primary')};
    outline: 0 none;
    background: none;
  }

  .react-calendar__navigation {
    margin-bottom: 20px;
  }

  .react-calendar__navigation__prev2-button,
  .react-calendar__navigation__next2-button {
    display: none;
  }

  .react-calendar__navigation__arrow {
    padding: 0 10px;
  }

  .react-calendar__navigation__arrow:disabled {
    opacity: 0;
  }

  .react-calendar__navigation__label {
    text-transform: uppercase;
  }

  .react-calendar__month-view__weekdays {
    margin-bottom: 7px;
    font-size: 14px;
    line-height: 1.14;
    color: ${palette('grayscale', 2, true)};
  }

  .react-calendar__month-view__days__day--neighboringMonth {
    color: ${palette('grayscale', 2, true)};
  }

  .react-calendar__tile {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border-radius: 50%;

    &:before {
      content: '';
      padding: 50% 0;
      display: inline-block;
    }

    &:disabled {
      color: ${palette('grayscale', 2, true)};
    }
  }

  .react-calendar__tile--now {
    border: 1px solid ${palette(1)};
  }

  .react-calendar__tile--active {
    background-color: ${palette(1)};
    color: ${palette('grayscale', 0, true)};
  }
`;

DatePicker.defaultProps = {
  palette: 'primary',
  testid: 'datepicker',
};
